import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeContextProvider } from './contexts/ThemeContext';
import { CssBaseline, Box, CircularProgress, Typography } from '@mui/material';
import { User } from 'firebase/auth';
import { onAuthStateChange } from './firebase';
import Header from './components/Header';
import Login from './components/Login';
import PullRequests from './pages/pullRequests/PullRequests';
import PullRequestRedirect from './pages/pullRequests/PullRequestRedirect';
import DeploymentStatus from './pages/deploymentStatus/DeploymentStatus';
import Deployments from './pages/deployments/Deployments';
import Environments from './pages/environments/Environments';
import EnvironmentDetail from './pages/environments/EnvironmentDetail';
import TestExecution from './pages/environments/testExecutions/TestExecution';
import MainBranch from './pages/mainBranch/MainBranch';
import ConfigEditorPage from './pages/config/ConfigEditorPage';
import MetricsPage from './pages/metrics/MetricsPage';
import { PullRequestsProvider } from './contexts/PullRequestsContext';
import { TestExecutionsProvider } from './contexts/TestExecutionsContext';
import { DeploymentStatusProvider } from './contexts/DeploymentStatusContext';
import { MainCommitsProvider } from './contexts/MainCommitsContext';
import { ConfigProvider } from './contexts/ConfigContext';
import { EnvironmentsProvider } from './contexts/EnvironmentsContext';
import { PoolsProvider } from './contexts/PoolsContext';

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const setupAuth = async () => {
      try {
        const unsubscribe = await onAuthStateChange((user) => {
          setUser(user);
          setLoading(false);
        });

        return unsubscribe;
      } catch (error) {
        console.error('Error setting up auth state change:', error);
        setLoading(false);
      }
    };

    setupAuth();
  }, []);

  if (loading) {
    return (
      <ThemeContextProvider>
        <CssBaseline />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body1" color="text.secondary">
            Loading...
          </Typography>
        </Box>
      </ThemeContextProvider>
    );
  }

  return (
    <ThemeContextProvider>
      <CssBaseline />
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <ConfigProvider user={user}>
          <EnvironmentsProvider user={user}>
          <PoolsProvider user={user}>
          <PullRequestsProvider user={user}>
            <TestExecutionsProvider user={user}>
            <MainCommitsProvider user={user}>
              <DeploymentStatusProvider user={user}>
              <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                <Header user={user} />
                <Box component="main" sx={{ flexGrow: 1, bgcolor: 'background.default' }}>
                  <Routes>
                    <Route 
                      path="/" 
                      element={user ? <Navigate to="/pull-requests" /> : <Navigate to="/login" />} 
                    />
                    <Route 
                      path="/login" 
                      element={user ? <Navigate to="/" /> : <Login />} 
                    />
                    <Route 
                      path="/pull-requests" 
                      element={user ? <PullRequests /> : <Navigate to="/login" />} 
                    />
                    <Route 
                      path="/pull-request/:prNumber" 
                      element={user ? <PullRequestRedirect /> : <Navigate to="/login" />} 
                    />
                    <Route 
                      path="/main-branch"
                      element={user ? <MainBranch /> : <Navigate to="/login" />}
                    />
                    <Route 
                      path="/deployments" 
                      element={user ? <Deployments /> : <Navigate to="/login" />} 
                    />
                    <Route 
                      path="/environments" 
                      element={user ? <Environments /> : <Navigate to="/login" />} 
                    />
                    <Route
                      path="/metrics"
                      element={user ? <MetricsPage /> : <Navigate to="/login" />}
                    />
                    <Route
                      path="/config/edit"
                      element={user ? <ConfigEditorPage /> : <Navigate to="/login" />}
                    />
                    <Route 
                      path="/environment/:environmentId" 
                      element={user ? <EnvironmentDetail /> : <Navigate to="/login" />} 
                    />
                    <Route 
                      path="/environment/:environmentId/test-execution/:testSuiteId" 
                      element={user ? <TestExecution /> : <Navigate to="/login" />} 
                    />
                    <Route 
                      path="/deployment/:deploymentId" 
                      element={user ? <DeploymentStatus /> : <Navigate to="/login" />} 
                    />
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                </Box>
              </Box>
              </DeploymentStatusProvider>
            </MainCommitsProvider>
            </TestExecutionsProvider>
          </PullRequestsProvider>
          </PoolsProvider>
          </EnvironmentsProvider>
        </ConfigProvider>
      </Router>
    </ThemeContextProvider>
  );
}

export default App; 